import type {
  UrlContextConfig,
  UrlForwardParamsConfig,
  UrlLinkDecorationConfig,
  UrlManagedParamConfig,
  UrlQueryParamsConfig,
} from '@/config/shared/url-context';

import { DEFAULT_URL_CONTEXT_SID_MAX_AGE_MS } from './constants';

export const DEFAULT_SID_MANAGED_PARAM: UrlManagedParamConfig = {
  key: 'public_session',
  param: 'sid',
  mode: 'read_write',
  writeStrategy: 'replaceState',
  generator: {
    type: 'public_id',
    prefix: 'sid',
  },
  persistence: {
    enabled: true,
    strategy: 'localStorage_cookie',
    storageKey: 'sid',
    cookie: {
      enabled: true,
      name: 'sid',
      domain: 'auto',
      path: '/',
      sameSite: 'Lax',
      secure: true,
      maxAgeMs: DEFAULT_URL_CONTEXT_SID_MAX_AGE_MS,
    },
  },
  decorateLinks: true,
};

export const DEFAULT_QUERY_PARAMS_WHEN_ENABLED: Required<
  Pick<UrlQueryParamsConfig, 'enabled' | 'mode' | 'mergeDefaultDenylist'>
> &
  UrlQueryParamsConfig = {
  enabled: true,
  mode: 'all_except',
  mergeDefaultDenylist: true,
};

export const DEFAULT_FORWARD_PARAMS_WHEN_ENABLED: Required<
  Pick<UrlForwardParamsConfig, 'enabled' | 'mode' | 'mergeDefaultDenylist'>
> &
  UrlForwardParamsConfig = {
  enabled: true,
  mode: 'all_except',
  mergeDefaultDenylist: true,
};

export const DEFAULT_LINK_DECORATION_WHEN_ENABLED: UrlLinkDecorationConfig = {
  enabled: true,
  strategy: 'on_click',
  selector: 'a[href]',
  allowedDomains: '*',
  mergeDefaultExcludedDomains: true,
  mergeDefaultExcludeSelectors: true,
  preserveExisting: true,
  overwriteExisting: false,
  decorateHashLinks: false,
  decorateMailto: false,
  decorateTel: false,
};

/**
 * Resolve config parcial → efetiva.
 * - `enabled: false` → feature off
 * - `enabled: true` sem nested → aplica defaults úteis (inclui sid)
 * - `managedParams: []` → não cria sid
 */
export function resolveUrlContextConfig(
  raw: UrlContextConfig | undefined
): Required<Pick<UrlContextConfig, 'enabled'>> & {
  queryParams: UrlQueryParamsConfig;
  managedParams: UrlManagedParamConfig[];
  forwardParams: UrlForwardParamsConfig;
  linkDecoration: UrlLinkDecorationConfig;
} {
  if (!raw || raw.enabled !== true) {
    return {
      enabled: false,
      queryParams: { enabled: false },
      managedParams: [],
      forwardParams: { enabled: false },
      linkDecoration: { enabled: false },
    };
  }

  const managedParams =
    raw.managedParams !== undefined
      ? raw.managedParams.map(cloneManagedParam)
      : [cloneManagedParam(DEFAULT_SID_MANAGED_PARAM)];

  return {
    enabled: true,
    queryParams: {
      ...DEFAULT_QUERY_PARAMS_WHEN_ENABLED,
      ...raw.queryParams,
      ...(raw.queryParams?.allowlist
        ? { allowlist: [...raw.queryParams.allowlist] }
        : {}),
      ...(raw.queryParams?.denylist
        ? { denylist: [...raw.queryParams.denylist] }
        : {}),
    },
    managedParams,
    forwardParams: {
      ...DEFAULT_FORWARD_PARAMS_WHEN_ENABLED,
      ...raw.forwardParams,
      ...(raw.forwardParams?.allowlist
        ? { allowlist: [...raw.forwardParams.allowlist] }
        : {}),
      ...(raw.forwardParams?.denylist
        ? { denylist: [...raw.forwardParams.denylist] }
        : {}),
    },
    linkDecoration: {
      ...DEFAULT_LINK_DECORATION_WHEN_ENABLED,
      ...raw.linkDecoration,
      ...(raw.linkDecoration?.allowedDomains &&
      raw.linkDecoration.allowedDomains !== '*'
        ? { allowedDomains: [...raw.linkDecoration.allowedDomains] }
        : {}),
      ...(raw.linkDecoration?.excludedDomains
        ? { excludedDomains: [...raw.linkDecoration.excludedDomains] }
        : {}),
      ...(raw.linkDecoration?.excludeSelectors
        ? { excludeSelectors: [...raw.linkDecoration.excludeSelectors] }
        : {}),
    },
  };
}

function cloneManagedParam(
  param: UrlManagedParamConfig
): UrlManagedParamConfig {
  return {
    ...param,
    ...(param.generator ? { generator: { ...param.generator } } : {}),
    ...(param.persistence
      ? {
          persistence: {
            ...param.persistence,
            ...(param.persistence.cookie
              ? { cookie: { ...param.persistence.cookie } }
              : {}),
          },
        }
      : {}),
  };
}
